import type { BadgeTone } from '@/components/ui'
import type { components } from './client'

export enum EventTypeType {
  Circle = 'Cercle',
  Class = 'Cours',
  Demonstration = 'Manifestation',
  Workshop = 'Atelier',
  Sport = 'Sport',
  Janaza = 'Janaza',
  Work = 'Travail',
  Social = 'Social',
}

export interface CategoryStyle {
  tone: BadgeTone
  /** Solid accent colour (banners, pins, left bars). */
  color: string
  /** Soft tint (chips, backgrounds). */
  tint: string
}

const TONE_STYLE: Record<string, CategoryStyle> = {
  emerald: {
    tone: 'emerald',
    color: 'var(--emerald-600)',
    tint: 'var(--emerald-100)',
  },
  gold: { tone: 'gold', color: 'var(--gold-600)', tint: 'var(--gold-100)' },
  turquoise: {
    tone: 'turquoise',
    color: 'var(--turquoise)',
    tint: 'var(--turquoise-tint)',
  },
  terracotta: {
    tone: 'terracotta',
    color: 'var(--terracotta)',
    tint: 'var(--terracotta-tint)',
  },
  indigo: {
    tone: 'indigo',
    color: 'var(--indigo)',
    tint: 'var(--indigo-tint)',
  },
  plum: { tone: 'plum', color: 'var(--plum)', tint: 'var(--plum-tint)' },
}

/** Maps each backend event type to a zellij jewel tone. */
const TYPE_TONE: Record<string, keyof typeof TONE_STYLE> = {
  [EventTypeType.Class]: 'indigo',
  [EventTypeType.Circle]: 'terracotta',
  [EventTypeType.Demonstration]: 'plum',
  [EventTypeType.Workshop]: 'gold',
  [EventTypeType.Sport]: 'turquoise',
  [EventTypeType.Janaza]: 'emerald',
  [EventTypeType.Work]: 'indigo',
  [EventTypeType.Social]: 'emerald',
}

/** Resolve the visual style for an event type name (emerald fallback). */
export function catOf(typeName: string | undefined): CategoryStyle {
  const tone = (typeName && TYPE_TONE[typeName]) || 'emerald'
  return TONE_STYLE[tone]
}

const TONE_NAMES = Object.keys(TONE_STYLE)

/** Jewel tone by index, cycling round-robin through the palette. */
export function toneOfIndex(i: number): CategoryStyle {
  const n = TONE_NAMES.length
  return TONE_STYLE[TONE_NAMES[((i % n) + n) % n]]
}

/** Deterministically map an arbitrary label (e.g. a free-form tag) to a jewel tone. */
export function toneOfLabel(label: string): CategoryStyle {
  let h = 0
  for (let i = 0; i < label.length; i++)
    h = (h * 31 + label.charCodeAt(i)) >>> 0
  return toneOfIndex(h)
}

export type Event = components['schemas']['Event']
export type EventType = components['schemas']['EventType']
export type Scraper = components['schemas']['Scraper']
export type Entity = components['schemas']['Entity']
export type EntityKind = components['schemas']['EntityKind']
